import { useState, useEffect, useMemo } from "react";

interface UseSelectValidationParams {
  submitted: boolean;
  selectedValue: string;
  required: boolean;
  requiredErrorMessage: string;
  errors: Set<string>;
  error?: boolean | string;
}

/**
 * Decides whether the select should render an error border, given an explicit
 * `error` flag/message and the set of collected validation errors.
 *
 * @param error Explicit error state: `true`, a message string, or undefined.
 * @param errors The set of accumulated validation error messages.
 * @returns `true` when the control should show its error styling.
 */
function computeHasErrorBorder(error: boolean | string | undefined, errors: Set<string>): boolean {
  if (error === true) return true;
  if (typeof error === 'string' && error.length > 0) return true;
  return errors.size > 0;
}

/**
 * Manages validation state for a select input: derives the required-field error
 * on submit, merges external errors, and computes the error-border flag.
 *
 * @param params Submission state, current value, required config, and errors.
 * @returns `{ allErrorMessages, hasErrorBorder }` for rendering.
 */
export function useSelectValidation({
  submitted,
  selectedValue,
  required,
  requiredErrorMessage,
  errors,
  error,
}: UseSelectValidationParams) {
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  useEffect(() => {
    const needsRequired = submitted && !selectedValue && required;
    setErrorMessages(needsRequired ? [requiredErrorMessage] : []);
  }, [submitted, selectedValue, required, requiredErrorMessage]);

  const allErrorMessages = useMemo(() => {
    const externalErrors = Array.from(errors);
    return externalErrors.length > 0 ? externalErrors : errorMessages;
  }, [errors, errorMessages]);

  const hasErrorBorder = useMemo(
    () => computeHasErrorBorder(error, errors),
    [error, errors],
  );

  return { allErrorMessages, hasErrorBorder };
}
